import {
    useLayoutEffect,
    useCallback,
    useContext,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    type PanResponderGestureState,
    type GestureResponderEvent,
    type LayoutChangeEvent,
    PanResponder,
    Animated,
    Platform,
    View
} from "react-native";
import type ISparklineProps from "./type";
import type {
    SparklineKeyboardEventType,
    SparklinePointerEventType,
    SparklineShape
} from "./type";
import stylesheet, {
    getSparklineTooltipStyle,
    getSparklineMeasures,
    useStyles
} from "./stylesheet";
import {
    getSparklineDomain,
    getSparklineShape,
    getSparklineIndex
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    useReducedMotion,
    MOTION_EASINGS,
    MOTION_INTENTS,
    usePresence
} from "../../helpers/motion";
import Svg, {
    LinearGradient,
    ClipPath,
    Circle,
    Defs,
    Rect,
    Path,
    Stop,
    G
} from "react-native-svg";
import {
    getChartStaggerProgress,
    getChartKeyboardIndex,
    CHART_STAGGER_LIMIT
} from "../chart/util";
import {
    getChartPlotTooltipMotionStyle
} from "../chart/components/plot/stylesheet";
import {
    formatNumber
} from "../../helpers/locale";
import {
    ChartContext
} from "../chart/context";
import useChartValueTransition from "../chart/useChartValueTransition";
import useChartEntrance from "../chart/useChartEntrance";
import Text from "../text";

const SPARKLINE_TOUCH_POINTER = "touch";

const SPARKLINE_ACCESSIBILITY_ACTIONS = [
    {
        name: "increment"
    },
    {
        name: "decrement"
    }
];

const Sparkline: FC<ISparklineProps> = ({
    isAnimatedOnMount: isAnimatedOnMountProp,
    isShowLastPoint = true,
    isInteractive = false,
    isDecorative = false,
    variant = "line",
    customLocalize,
    sequentialStep,
    size = "small",
    valueFormatter,
    customTheme,
    onLayout,
    summary,
    values,
    style,
    ...props
}) => {
    const context = useContext(ChartContext);

    const theme = NCoreUIKitTheme.useContext(customTheme);

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = theme;

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isRTL = direction === "rtl";

    const isWeb = Platform.OS === "web";

    const isReduceMotionEnabled = useReducedMotion();

    const {
        surfaceColor: surfaceColorKey
    } = NCoreUIKitTheme.useContext();

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const clipID = `${baseID}-clip`;

    const gradientID = `${baseID}-fill`;

    const liveID = `${baseID}-live`;

    const containerRef = useRef<View>(null);

    const activeIndexRef = useRef<number | undefined>(undefined);

    const shapeRef = useRef<SparklineShape | undefined>(undefined);

    const [
        layout,
        setLayout
    ] = useState<{ height: number; width: number }>({
        height: 0,
        width: 0
    });

    const [
        activeIndex,
        setActiveIndex
    ] = useState<number | undefined>(undefined);

    const [
        inputSource,
        setInputSource
    ] = useState<"pointer" | "keyboard" | "touch" | undefined>(undefined);

    const [
        tooltipSize,
        setTooltipSize
    ] = useState<{ height: number; width: number } | undefined>(undefined);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState();

    const count = values.length;

    const [
        low,
        high
    ] = count ? getSparklineDomain({
        variant,
        values
    }) : [
        0,
        1
    ];

    const entrance = useChartEntrance({
        isEnabled: isAnimatedOnMountProp ?? context?.isAnimatedOnMount ?? false,
        isReady: count > 0 && layout.width > 0,
        targetRef: containerRef,
        customTheme
    });

    const transition = useChartValueTransition({
        shapeKey: `${variant}|${count}`,
        values: [
            ...values,
            low,
            high
        ],
        customTheme
    });

    const {
        container: containerDynamicStyle,
        live: liveDynamicStyle,
        color: accentColor,
        muted: mutedColor,
        area: areaColor
    } = useStyles({
        isInteractive: isInteractive && count > 0,
        step: sequentialStep,
        controlHeights,
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        radiuses,
        borders,
        colors,
        size
    });

    const haloColor = colors.content.container[surfaceColorKey];

    const measures = getSparklineMeasures({
        shapeConfigs,
        radiuses,
        borders
    });

    const formatValue = valueFormatter ?? ((value: number) => formatNumber({
        options: {
            maximumFractionDigits: 2
        },
        locale: activeLocale,
        value
    }));

    const lineEnd = durations.long / (durations.long + CHART_STAGGER_LIMIT * durations.stagger);

    const dotEasing = MOTION_EASINGS[MOTION_INTENTS.chartEnter.easing];

    const dotProgress = entrance.progress >= 1 ? 1 : dotEasing(Math.max(0, Math.min(1, (entrance.progress - lineEnd) / (1 - lineEnd))));

    const lineProgress = getChartStaggerProgress({
        progress: entrance.progress,
        stagger: durations.stagger,
        duration: durations.long,
        intent: "chartDraw",
        count: 1,
        index: 0
    });

    const getShape = (layerValues: Array<number>): SparklineShape => getSparklineShape({
        values: layerValues.slice(0, layerValues.length - 2),
        getBarProgress: (index) => getChartStaggerProgress({
            progress: entrance.progress,
            stagger: durations.stagger,
            duration: durations.long,
            index,
            count
        }),
        high: layerValues[layerValues.length - 1] ?? high,
        low: layerValues[layerValues.length - 2] ?? low,
        height: layout.height,
        width: layout.width,
        measures,
        variant,
        isRTL
    });

    const shape = useMemo(() => getShape(transition.values), [
        transition.values,
        entrance.progress,
        layout.height,
        layout.width,
        variant,
        isRTL
    ]);

    shapeRef.current = shape;

    const previousShape = transition.previous && layout.width > 0 ? getShape(transition.previous.values) : undefined;

    const first = values[0];

    const last = values[count - 1];

    const currentSummary = summary ?? (first === undefined || last === undefined ? undefined : localize("chart-trend", [
        formatValue(first),
        formatValue(last)
    ]));

    const isActive = isInteractive && count > 0;

    const activeValue = activeIndex === undefined ? undefined : values[activeIndex];

    const reading = activeValue === undefined || inputSource === "pointer" ? "" : formatValue(activeValue);

    const highlightIndex = activeIndex !== undefined && activeIndex < count ? activeIndex : isShowLastPoint && count ? count - 1 : undefined;

    const handleActiveChange = (index: number | undefined, source: "pointer" | "keyboard" | "touch") => {
        setInputSource(source);

        if(index !== activeIndexRef.current) {
            activeIndexRef.current = index;

            setActiveIndex(index);
        }
    };

    const handlePoint = (x: number, source: "pointer" | "touch") => {
        const points = shapeRef.current?.points;

        if(!isActive || !points) {
            return;
        }

        handleActiveChange(getSparklineIndex({
            points,
            x
        }), source);
    };

    const handlePointRef = useRef(handlePoint);

    handlePointRef.current = handlePoint;

    const handleLayout = (event: LayoutChangeEvent) => {
        setLayout({
            height: event.nativeEvent.layout.height,
            width: event.nativeEvent.layout.width
        });

        onLayout?.(event);
    };

    useLayoutEffect(() => {
        if(!isWeb) {
            return;
        }

        const node: unknown = containerRef.current;

        if(typeof node !== "object" || node === null || !("getBoundingClientRect" in node) || typeof node.getBoundingClientRect !== "function") {
            return;
        }

        const rect = node.getBoundingClientRect() as {
            height: number;
            width: number;
        };

        setLayout((current) => current.height === rect.height && current.width === rect.width ? current : {
            height: rect.height,
            width: rect.width
        });
    }, [
        isWeb
    ]);

    const getPointerSource = (event: SparklinePointerEventType): "pointer" | "touch" => {
        return event.nativeEvent.pointerType === SPARKLINE_TOUCH_POINTER ? "touch" : "pointer";
    };

    const onPointerMove = (event: SparklinePointerEventType) => {
        handlePoint(event.nativeEvent.clientX - event.currentTarget.getBoundingClientRect().left, getPointerSource(event));
    };

    const onPointerLeave = (event: SparklinePointerEventType) => {
        if(getPointerSource(event) === "touch") {
            return;
        }

        handleActiveChange(undefined, "pointer");
    };

    const onKeyDown = (event: SparklineKeyboardEventType) => {
        if(!isActive) {
            return;
        }

        if(event.key === "Escape") {
            if(activeIndexRef.current === undefined) {
                return;
            }

            handleActiveChange(undefined, "keyboard");

            event.preventDefault();

            return;
        }

        const nextIndex = getChartKeyboardIndex({
            direction: isRTL ? "rtl" : "ltr",
            index: activeIndexRef.current,
            key: event.key,
            count
        });

        if(nextIndex === undefined) {
            return;
        }

        handleActiveChange(nextIndex, "keyboard");

        event.preventDefault();
    };

    const handleStep = (step: number) => {
        const current = activeIndexRef.current;

        handleActiveChange(current === undefined ? step > 0 ? 0 : count - 1 : Math.min(count - 1, Math.max(0, current + step)), "keyboard");
    };

    const onBlur: typeof interactionHandlers.onBlur = (event) => {
        interactionHandlers.onBlur(event);

        handleActiveChange(undefined, "keyboard");
    };

    const handleTouch = useCallback((event: GestureResponderEvent, gestureState?: PanResponderGestureState) => {
        handlePointRef.current(gestureState ? event.nativeEvent.locationX + gestureState.dx : event.nativeEvent.locationX, "touch");
    }, []);

    const panResponder = useMemo(() => PanResponder.create({
        onMoveShouldSetPanResponder: (_event, gestureState) => Math.abs(gestureState.dx) > Math.abs(gestureState.dy),
        onPanResponderMove: (event, gestureState) => handleTouch(event, gestureState),
        onPanResponderRelease: () => handleActiveChange(undefined, "touch"),
        onStartShouldSetPanResponder: () => !isWeb && isActive,
        onPanResponderGrant: (event) => handleTouch(event),
        onPanResponderTerminationRequest: () => true
    }), [
        handleTouch,
        isActive,
        isWeb
    ]);

    const anchor = activeIndex === undefined ? undefined : shape.points[activeIndex];

    const {
        isRendered: isTooltipRendered,
        progress: tooltipProgress
    } = usePresence({
        isVisible: isActive && !!anchor && activeValue !== undefined,
        enterIntent: "surfaceEnter",
        exitIntent: "surfaceExit",
        customTheme
    });

    const tooltipSnapshotRef = useRef<{ text: string; x: number; y: number } | undefined>(undefined);

    if(anchor && activeValue !== undefined) {
        tooltipSnapshotRef.current = {
            text: formatValue(activeValue),
            x: anchor.x,
            y: anchor.y
        };
    }

    const renderTooltip = () => {
        const snapshot = tooltipSnapshotRef.current;

        if(!snapshot || !isTooltipRendered) {
            return null;
        }

        const left = tooltipSize ? Math.max(0, Math.min(layout.width - tooltipSize.width, snapshot.x - tooltipSize.width / 2)) : 0;

        const top = tooltipSize ? snapshot.y - tooltipSize.height - spaces.spacingMd : 0;

        return <Animated.View
            style={[
                stylesheet.tooltip,
                getSparklineTooltipStyle({
                    controlPaddings,
                    shapeConfigs,
                    shadowAlpha,
                    elevations,
                    radiuses,
                    borders,
                    colors,
                    spaces
                }),
                getChartPlotTooltipMotionStyle({
                    progress: tooltipProgress,
                    offset: spaces.spacingXs,
                    isReduceMotionEnabled,
                    isOnEnd: true,
                    left,
                    top
                }),
                tooltipSize ? undefined : stylesheet.unmeasured
            ]}
            onLayout={(event) => setTooltipSize({
                height: event.nativeEvent.layout.height,
                width: event.nativeEvent.layout.width
            })}
        >
            <Text
                customTheme={customTheme}
                variant="titleSmallSize"
                numberOfLines={1}
                color="high"
            >
                {snapshot.text}
            </Text>
        </Animated.View>;
    };

    const renderLayer = (layerShape: SparklineShape, opacity: number, isCurrent: boolean) => {
        const highlight = isCurrent && highlightIndex !== undefined ? layerShape.points[highlightIndex] : undefined;

        const dotScale = activeIndex === undefined ? dotProgress : 1;

        const isClipped = isCurrent && lineProgress < 1 && variant !== "bars";

        const bars = layerShape.bars.map((bar, index) => <Path
            fill={isCurrent && index === highlightIndex ? accentColor : mutedColor}
            d={bar.path}
            key={index}
        />);

        return <G
            opacity={opacity}
        >
            {
                variant === "bars" ?
                    bars
                :
                    <G
                        clipPath={isClipped ? `url(#${clipID})` : undefined}
                    >
                        {
                            variant === "area" ?
                                <Path
                                    fill={`url(#${gradientID})`}
                                    d={layerShape.area}
                                />
                            :
                                null
                        }
                        <Path
                            stroke={variant === "area" ? accentColor : mutedColor}
                            strokeWidth={measures.lineWidth}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            d={layerShape.line}
                            fill="none"
                        />
                    </G>
            }
            {
                highlight && variant !== "bars" && dotScale > 0 ?
                    <>
                        <Circle
                            r={(measures.dotRadius + measures.haloWidth) * dotScale}
                            fill={haloColor}
                            cx={highlight.x}
                            cy={highlight.y}
                        />
                        <Circle
                            r={measures.dotRadius * dotScale}
                            fill={accentColor}
                            cx={highlight.x}
                            cy={highlight.y}
                        />
                    </>
                :
                    null
            }
        </G>;
    };

    const accessibleName = currentSummary ?? "";

    const a11yProps = isActive ? isWeb ? {
        dataSet: interactionHandlers.dataSet,
        "aria-label": accessibleName,
        onPointerDown: onPointerMove,
        role: "application" as const,
        tabIndex: 0 as const,
        onPointerLeave,
        onPointerMove,
        onKeyDown
    } : {
        onAccessibilityAction: ({
            nativeEvent
        }: { nativeEvent: { actionName: string } }) => {
            if(nativeEvent.actionName === "increment") {
                handleStep(1);
            } else if(nativeEvent.actionName === "decrement") {
                handleStep(-1);
            }
        },
        accessibilityActions: SPARKLINE_ACCESSIBILITY_ACTIONS,
        accessibilityRole: "adjustable" as const,
        accessibilityLabel: accessibleName,
        accessibilityValue: reading ? {
            text: reading
        } : undefined,
        accessible: true
    } : !isDecorative && currentSummary ? {
        accessibilityLabel: currentSummary,
        "aria-label": currentSummary,
        role: "img" as const
    } : {
        "aria-hidden": true
    };

    const clipX = isRTL ? layout.width * (1 - lineProgress) : 0;

    const clipPadding = measures.inset;

    return <View
        {...props}
        {...a11yProps}
        {...(isWeb || !isActive ? {} : panResponder.panHandlers)}
        onFocus={isActive ? interactionHandlers.onFocus : undefined}
        onBlur={isActive ? onBlur : undefined}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        onLayout={handleLayout}
        focusable={isActive}
        ref={containerRef}
    >
        {
            layout.width > 0 && count > 0 ?
                <View
                    importantForAccessibility="no-hide-descendants"
                    accessibilityElementsHidden={true}
                    style={[
                        stylesheet.svg,
                        {
                            opacity: entrance.opacity
                        }
                    ]}
                    aria-hidden={true}
                >
                    <Svg
                        height={layout.height}
                        width={layout.width}
                    >
                        <Defs>
                            <ClipPath
                                id={clipID}
                            >
                                <Rect
                                    width={layout.width * lineProgress + clipPadding}
                                    height={layout.height + clipPadding * 2}
                                    x={clipX - (isRTL ? 0 : clipPadding)}
                                    y={-clipPadding}
                                />
                            </ClipPath>
                            <LinearGradient
                                id={gradientID}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <Stop
                                    stopColor={areaColor}
                                    offset="0"
                                />
                                <Stop
                                    stopColor={areaColor}
                                    stopOpacity={0}
                                    offset="1"
                                />
                            </LinearGradient>
                        </Defs>
                        {
                            previousShape && transition.previous ?
                                renderLayer(previousShape, transition.previous.opacity, false)
                            :
                                null
                        }
                        {renderLayer(shape, transition.opacity, true)}
                    </Svg>
                </View>
            :
                null
        }
        {isActive ? renderTooltip() : null}
        {
            isActive && isWeb ?
                <Text
                    style={[
                        stylesheet.live,
                        liveDynamicStyle
                    ]}
                    aria-live="polite"
                    id={liveID}
                >
                    {inputSource === "pointer" ? "" : reading}
                </Text>
            :
                null
        }
    </View>;
};
export default Sparkline;
